import { TAdminMenuCounterKey } from '@/shared/api/admin';

export const ADMIN_ROUTES = {
  root: '/admin-v2',
  login: '/admin-v2/login',
  password_reset: '/admin-v2/password-reset',
  settings: '/admin-v2/settings',
  comments: '/admin-v2/comments',
  users: {
    index: '/admin-v2/users',
    /**
     * Маршрут карточки пользователя
     * @param id ID пользователя или route placeholder
     */
    show: (id = ':id') => `/admin-v2/users/${id}`,
    admins: '/admin-v2/admin-users'
  },
  moderation: {
    actor_complaints: '/admin-v2/moderation/actor-complaints',
    participant_complaints: '/admin-v2/moderation/participant-complaints',
    images: '/admin-v2/moderation/images',
    videos: '/admin-v2/moderation/videos'
  },
  companies: '/admin-v2/companies',
  actor_cards: '/admin-v2/actor-cards',
  legal_entities: '/admin-v2/legal-entities',
  projects: {
    index: '/admin-v2/projects',
    /**
     * Маршрут карточки проекта
     * @param id ID проекта или route placeholder
     */
    show: (id = ':id') => `/admin-v2/projects/${id}`
  },
  castings: {
    index: '/admin-v2/castings',
    /**
     * Маршрут карточки кастинга
     * @param id ID кастинга или route placeholder
     */
    show: (id = ':id') => `/admin-v2/castings/${id}`,
    new: '/admin-v2/castings/new',
    /**
     * Маршрут редактирования кастинга
     * @param id ID кастинга или route placeholder
     */
    edit: (id = ':id') => `/admin-v2/castings/${id}/edit`
  },
  responses: '/admin-v2/responses',
  project_participants: '/admin-v2/project-participants',
  invitations: '/admin-v2/invitations',
  feedbacks: '/admin-v2/feedbacks',
  films: '/admin-v2/films',
  imported_films: '/admin-v2/imported-films',
  documents: {
    templates: '/admin-v2/documents/templates'
  },
  legal_docs: '/admin-v2/legal-docs',
  excerpts: '/admin-v2/excerpts',
  landing: {
    news: '/admin-v2/landing/news',
    news_images: '/admin-v2/landing/news-images',
    news_tags: '/admin-v2/landing/news-tags',
    tabs: '/admin-v2/landing/tabs',
    faqs: '/admin-v2/landing/faqs',
    castings: '/admin-v2/landing/castings',
    logos: '/admin-v2/landing/logos'
  },
  dictionaries: {
    specializations: '/admin-v2/specializations',
    role_types: '/admin-v2/role-types',
    occupations: '/admin-v2/occupations',
    project_categories: '/admin-v2/project-categories',
    skills: '/admin-v2/skills',
    languages: '/admin-v2/languages',
    interests: '/admin-v2/interests',
    cities: '/admin-v2/cities',
    filmmakers: '/admin-v2/filmmakers',
    tags: '/admin-v2/tags'
  },
  education: {
    institutions: '/admin-v2/educational-institutions'
  },
  system: {
    mkrf_imports: '/admin-v2/system/mkrf-imports',
    devices: '/admin-v2/system/devices',
    notifications: '/admin-v2/system/notifications',
    chat: '/admin-v2/system/chat',
    bookmarks: '/admin-v2/system/bookmarks'
  }
} as const;

/** Бейдж-счётчик на пункте меню админки */
export interface IAdminNavCounter {
  /** Куда ведёт клик по бейджу; без него бейдж — просто число */
  href?: string;
  /** Ключ счётчика из GET /api/admin/v1/menu_counters */
  key: TAdminMenuCounterKey;
  /**
   * Человекочитаемая подпись бейджа для aria-label и подсказки («Подпись: N»); без
   * неё бейдж не становится кликабельным даже при заданном href —
   * `role="link"` без доступного имени бесполезен для скринридера
   */
  label?: string;
  /**
   * Тон бейджа: по умолчанию красный; `warning` — оранжевый, для второй очереди на том же пункте,
   * чтобы два бейджа различались без наведения
   */
  tone?: 'warning';
}

export type TAdminNavItem =
  | {
      /**
       * Бейджи-счётчики пункта: каждый ненулевой — отдельная пилюля в порядке массива
       * (у «Фильмов в проекты» две разные очереди — зависшие перегоны и удостоверения
       * на проверке, у каждой свой фильтр списка; сумма в одном бейдже вела бы не туда)
       */
      counters?: IAdminNavCounter[];
      href: string;
      icon: string;
      key: string;
      label: string;
      type: 'item';
    }
  | {
      children: Array<{
        href: string;
        key: string;
        label: string;
      }>;
      icon: string;
      key: string;
      label: string;
      type: 'group';
    }
  | {
      label: string;
      type: 'label';
    };

export interface IAdminRouteMeta {
  title: string;
  to: string;
  // Запись существует в ADMIN_ROUTE_META только ради статического фоллбека
  // заголовка крошки (см. shared/lib/adminPageTitle.ts) — маршрут для неё уже
  // производится где-то ещё (напр. detail-роутом list-конфига того же
  // ресурса), поэтому у неё нет своего конфига в ADMIN_GENERIC_RESOURCE_CONFIG,
  // и роутер (`src/app/router/admin.tsx`) обязан пропускать такие записи мимо
  // createGenericResourceRoute (19058, волна 3, fix-review F9)
  titleOnly?: boolean;
}

export const ADMIN_NAV_ITEMS: TAdminNavItem[] = [
  { type: 'label', label: 'Пользователи' },
  {
    type: 'item',
    key: 'users',
    label: 'Все пользователи',
    icon: 'users',
    href: ADMIN_ROUTES.users.index
  },
  {
    type: 'item',
    key: 'admins',
    label: 'Администраторы',
    icon: 'shield',
    href: ADMIN_ROUTES.users.admins
  },
  {
    type: 'item',
    key: 'comments',
    label: 'Комментарии',
    icon: 'message-square',
    href: ADMIN_ROUTES.comments
  },
  {
    type: 'group',
    key: 'moderation',
    label: 'Модерация портфолио',
    icon: 'eye',
    children: [
      {
        key: 'moderation/images',
        label: 'Фото',
        href: ADMIN_ROUTES.moderation.images
      },
      {
        key: 'moderation/videos',
        label: 'Видео',
        href: ADMIN_ROUTES.moderation.videos
      }
    ]
  },
  {
    type: 'item',
    key: 'moderation/actor-complaints',
    label: 'Жалобы на анкеты',
    icon: 'flag',
    href: ADMIN_ROUTES.moderation.actor_complaints,
    counters: [{ key: 'actor_complaints_new' }]
  },
  {
    type: 'item',
    key: 'moderation/participant-complaints',
    label: 'Жалобы на участников',
    icon: 'flag',
    href: ADMIN_ROUTES.moderation.participant_complaints,
    counters: [{ key: 'participant_complaints_new' }]
  },
  { type: 'label', label: 'Компании' },
  {
    type: 'item',
    key: 'companies',
    label: 'Компании',
    icon: 'briefcase',
    href: ADMIN_ROUTES.companies
  },
  {
    type: 'item',
    key: 'actor-cards',
    label: 'Реестр актёров',
    icon: 'users',
    href: ADMIN_ROUTES.actor_cards
  },
  {
    type: 'item',
    key: 'legal-entities',
    label: 'Юридические лица',
    icon: 'building-2',
    href: ADMIN_ROUTES.legal_entities
  },
  { type: 'label', label: 'Проекты и кастинги' },
  {
    type: 'item',
    key: 'projects',
    label: 'Проекты',
    icon: 'film',
    href: ADMIN_ROUTES.projects.index
  },
  {
    type: 'item',
    key: 'castings',
    label: 'Кастинги',
    icon: 'clapperboard',
    href: ADMIN_ROUTES.castings.index
  },
  {
    type: 'item',
    key: 'responses',
    label: 'Отклики',
    icon: 'inbox',
    href: ADMIN_ROUTES.responses
  },
  {
    type: 'item',
    key: 'project-participants',
    label: 'Участники проектов',
    icon: 'check-square',
    href: ADMIN_ROUTES.project_participants
  },
  {
    type: 'item',
    key: 'invitations',
    label: 'Приглашения',
    icon: 'send',
    href: ADMIN_ROUTES.invitations
  },
  { type: 'label', label: 'Контент и медиа' },
  {
    type: 'item',
    key: 'films',
    label: 'Фильмы (МКРФ)',
    icon: 'video',
    href: ADMIN_ROUTES.films
  },
  {
    type: 'item',
    key: 'imported-films',
    label: 'Фильмы в проекты',
    icon: 'tv',
    href: ADMIN_ROUTES.imported_films,
    counters: [
      {
        key: 'imported_films_stuck',
        href: `${ADMIN_ROUTES.imported_films}?stuck=true`,
        label: 'Зависшие перегоны'
      },
      {
        key: 'imported_films_unf_review',
        href: `${ADMIN_ROUTES.imported_films}?unf_review=true`,
        label: 'Удостоверения на проверке',
        tone: 'warning'
      }
    ]
  },
  {
    type: 'item',
    key: 'documents',
    label: 'Шаблоны документов',
    icon: 'file-text',
    href: ADMIN_ROUTES.documents.templates
  },
  {
    type: 'item',
    key: 'legal-docs',
    label: 'Правовые документы',
    icon: 'scroll-text',
    href: ADMIN_ROUTES.legal_docs
  },
  {
    type: 'item',
    key: 'excerpts',
    label: 'Видео читка',
    icon: 'file-music',
    href: ADMIN_ROUTES.excerpts
  },
  { type: 'label', label: 'Лендинг' },
  {
    type: 'group',
    key: 'landing',
    label: 'Landing',
    icon: 'globe',
    children: [
      {
        key: 'landing/news',
        label: 'Новости',
        href: ADMIN_ROUTES.landing.news
      },
      {
        key: 'landing/news-images',
        label: 'Изображения новостей',
        href: ADMIN_ROUTES.landing.news_images
      },
      {
        key: 'landing/news-tags',
        label: 'Теги новостей',
        href: ADMIN_ROUTES.landing.news_tags
      },
      {
        key: 'landing/tabs',
        label: 'Вкладки',
        href: ADMIN_ROUTES.landing.tabs
      },
      {
        key: 'landing/faqs',
        label: 'FAQ',
        href: ADMIN_ROUTES.landing.faqs
      },
      {
        key: 'landing/castings',
        label: 'Кастинги лендинга',
        href: ADMIN_ROUTES.landing.castings
      },
      {
        key: 'landing/logos',
        label: 'Логотипы',
        href: ADMIN_ROUTES.landing.logos
      }
    ]
  },
  { type: 'label', label: 'Справочники' },
  {
    type: 'group',
    key: 'refs',
    label: 'Все справочники',
    icon: 'list-tree',
    children: [
      {
        key: 'specializations',
        label: 'Специализации',
        href: ADMIN_ROUTES.dictionaries.specializations
      },
      {
        key: 'role-types',
        label: 'Типы ролей',
        href: ADMIN_ROUTES.dictionaries.role_types
      },
      {
        key: 'occupations',
        label: 'Профессии',
        href: ADMIN_ROUTES.dictionaries.occupations
      },
      {
        key: 'project-categories',
        label: 'Категории проектов',
        href: ADMIN_ROUTES.dictionaries.project_categories
      },
      {
        key: 'skills',
        label: 'Навыки',
        href: ADMIN_ROUTES.dictionaries.skills
      },
      {
        key: 'languages',
        label: 'Языки',
        href: ADMIN_ROUTES.dictionaries.languages
      },
      {
        key: 'cities',
        label: 'Города',
        href: ADMIN_ROUTES.dictionaries.cities
      },
      {
        key: 'educational-institutions',
        label: 'Учебные заведения',
        href: ADMIN_ROUTES.education.institutions
      },
      {
        key: 'filmmakers',
        label: 'Кинематографисты',
        href: ADMIN_ROUTES.dictionaries.filmmakers
      }
    ]
  },
  { type: 'label', label: 'Система' },
  {
    type: 'item',
    key: 'system/mkrf',
    label: 'Импорты МКРФ',
    icon: 'database',
    href: ADMIN_ROUTES.system.mkrf_imports
  },
  {
    type: 'item',
    key: 'system/devices',
    label: 'Устройства',
    icon: 'smartphone',
    href: ADMIN_ROUTES.system.devices
  },
  {
    type: 'item',
    key: 'system/notifications',
    label: 'Уведомления',
    icon: 'bell-ring',
    href: ADMIN_ROUTES.system.notifications
  },
  {
    type: 'item',
    key: 'system/bookmarks',
    label: 'Закладки',
    icon: 'bookmark',
    href: ADMIN_ROUTES.system.bookmarks
  },
  {
    type: 'item',
    key: 'feedbacks',
    label: 'Обратная связь',
    icon: 'message-square',
    href: ADMIN_ROUTES.feedbacks,
    counters: [{ key: 'feedbacks_new' }]
  },
  { type: 'label', label: 'Настройки' },
  {
    type: 'item',
    key: 'settings',
    label: 'Настройки админки',
    icon: 'settings',
    href: ADMIN_ROUTES.settings
  }
];

export const ADMIN_ROUTE_META: IAdminRouteMeta[] = [
  {
    title: 'Все пользователи',
    to: ADMIN_ROUTES.users.index
  },
  {
    title: 'Профиль пользователя',
    to: ADMIN_ROUTES.users.show()
  },
  {
    title: 'Администраторы',
    to: ADMIN_ROUTES.users.admins
  },
  {
    title: 'Комментарии',
    to: ADMIN_ROUTES.comments
  },
  {
    title: 'Модерация фото',
    to: ADMIN_ROUTES.moderation.images
  },
  {
    title: 'Модерация видео',
    to: ADMIN_ROUTES.moderation.videos
  },
  {
    title: 'Жалобы на анкеты',
    to: ADMIN_ROUTES.moderation.actor_complaints
  },
  {
    // Статический фоллбек крошки до загрузки дела (имя актёра публикует карточка
    // через adminPageTitle); роут создаёт detail-роут list-конфига — см. titleOnly
    title: 'Дело',
    to: `${ADMIN_ROUTES.moderation.actor_complaints}/:id`,
    titleOnly: true
  },
  {
    title: 'Жалобы на участников',
    to: ADMIN_ROUTES.moderation.participant_complaints
  },
  {
    // Фоллбек крошки до загрузки дела; имя участника публикует карточка
    title: 'Дело',
    to: `${ADMIN_ROUTES.moderation.participant_complaints}/:id`,
    titleOnly: true
  },
  {
    title: 'Компании',
    to: ADMIN_ROUTES.companies
  },
  {
    title: 'Реестр актёров',
    to: ADMIN_ROUTES.actor_cards
  },
  {
    title: 'Юридические лица',
    to: ADMIN_ROUTES.legal_entities
  },
  {
    title: 'Проекты',
    to: ADMIN_ROUTES.projects.index
  },
  {
    title: 'Проект',
    to: ADMIN_ROUTES.projects.show()
  },
  {
    title: 'Кастинги',
    to: ADMIN_ROUTES.castings.index
  },
  {
    title: 'Кастинг',
    to: ADMIN_ROUTES.castings.show()
  },
  {
    title: 'Создание кастинга',
    to: ADMIN_ROUTES.castings.new
  },
  {
    title: 'Редактирование кастинга',
    to: ADMIN_ROUTES.castings.edit()
  },
  {
    title: 'Отклики',
    to: ADMIN_ROUTES.responses
  },
  {
    title: 'Участники проектов',
    to: ADMIN_ROUTES.project_participants
  },
  {
    title: 'Приглашения',
    to: ADMIN_ROUTES.invitations
  },
  {
    title: 'Фильмы (МКРФ)',
    to: ADMIN_ROUTES.films
  },
  {
    title: 'Фильмы в проекты',
    to: ADMIN_ROUTES.imported_films
  },
  {
    // Без переопределения из adminPageTitle — статический фоллбек, чтобы до
    // загрузки записи топбар не падал в «Админ» (см. shared/lib/adminPageTitle.ts).
    // titleOnly: роут для этого пути уже создаёт genericResourceDetailRoutes
    // от list-конфига `imported_films` — своего конфига у записи нет и не
    // будет (см. IAdminRouteMeta.titleOnly).
    title: 'Фильм',
    to: `${ADMIN_ROUTES.imported_films}/:id`,
    titleOnly: true
  },
  {
    title: 'Шаблоны документов',
    to: ADMIN_ROUTES.documents.templates
  },
  {
    title: 'Правовые документы',
    to: ADMIN_ROUTES.legal_docs
  },
  {
    title: 'Видео читка',
    to: ADMIN_ROUTES.excerpts
  },
  {
    title: 'Новости лендинга',
    to: ADMIN_ROUTES.landing.news
  },
  {
    title: 'Изображения новостей',
    to: ADMIN_ROUTES.landing.news_images
  },
  {
    title: 'Теги новостей',
    to: ADMIN_ROUTES.landing.news_tags
  },
  {
    title: 'Вкладки лендинга',
    to: ADMIN_ROUTES.landing.tabs
  },
  {
    title: 'FAQ лендинга',
    to: ADMIN_ROUTES.landing.faqs
  },
  {
    title: 'Кастинги лендинга',
    to: ADMIN_ROUTES.landing.castings
  },
  {
    title: 'Логотипы',
    to: ADMIN_ROUTES.landing.logos
  },
  {
    title: 'Специализации',
    to: ADMIN_ROUTES.dictionaries.specializations
  },
  {
    title: 'Типы ролей',
    to: ADMIN_ROUTES.dictionaries.role_types
  },
  {
    title: 'Профессии',
    to: ADMIN_ROUTES.dictionaries.occupations
  },
  {
    title: 'Категории проектов',
    to: ADMIN_ROUTES.dictionaries.project_categories
  },
  {
    title: 'Навыки',
    to: ADMIN_ROUTES.dictionaries.skills
  },
  {
    title: 'Языки',
    to: ADMIN_ROUTES.dictionaries.languages
  },
  {
    title: 'Интересы',
    to: ADMIN_ROUTES.dictionaries.interests
  },
  {
    title: 'Города',
    to: ADMIN_ROUTES.dictionaries.cities
  },
  {
    title: 'Кинематографисты',
    to: ADMIN_ROUTES.dictionaries.filmmakers
  },
  {
    title: 'Теги',
    to: ADMIN_ROUTES.dictionaries.tags
  },
  {
    title: 'Учебные заведения',
    to: ADMIN_ROUTES.education.institutions
  },
  {
    title: 'Импорты МКРФ',
    to: ADMIN_ROUTES.system.mkrf_imports
  },
  {
    title: 'Устройства',
    to: ADMIN_ROUTES.system.devices
  },
  {
    title: 'Уведомления',
    to: ADMIN_ROUTES.system.notifications
  },
  {
    title: 'Чат-интеграция',
    to: ADMIN_ROUTES.system.chat
  },
  {
    title: 'Закладки',
    to: ADMIN_ROUTES.system.bookmarks
  },
  {
    title: 'Обратная связь',
    to: ADMIN_ROUTES.feedbacks
  },
  {
    title: 'Настройки админки',
    to: ADMIN_ROUTES.settings
  }
];
